import { Moment } from 'moment';
import { useState } from 'react';
import {
	Calendar,
	EventMember,
	EventMemberState,
	useLazyCalendarEventsQuery,
	useLazyWorkplaceEventsCalendarEventsQuery,
	User,
} from '../../../graphql/graphql';
import { CalendarEvent } from '../Components/Calendar/Calendar';
import { useEffectAsync } from '../../../helpers';
import { getMomentDate } from '../../../Utils/dates.util';

export function useCalendarEvents(calendar: Calendar) {
	const [dateRange, setDateRange] = useState<
		{ start: Moment; end: Moment } | undefined
	>();
	const [isLoading, setIsLoading] = useState(false);
	const [events, setEvents] = useState<CalendarEvent[]>([]);
	const [getCalendarEvents] = useLazyCalendarEventsQuery();

	//FUNCTION TO PREPARE PATIENT DATA
	const preparePatientData = (user?: User) => {
		if (!user) {
			return;
		}
		const profile = user.profile!;
		return {
			patient: {
				id: user.id,
				name: profile.fullName,
				age: profile.age,
				birthNumber: profile.birthNumber,
				phone: user.formattedPhoneNumber,
				isChild: user.isChild,
			},
		};
	};

	//FUNCTION TO PREPARE DOCTOR DATA
	const prepareDoctorData = (user?: User) => {
		if (!user) {
			return;
		}
		const profile = user.profile!;
		return {
			doctor: {
				id: user.id,
				name: profile.fullName,
			},
		};
	};

	//FUNCTION TO PREPARE EVENT MEMBERS DATA
	const prepareEventMembersData = (members?: EventMember[] | null) => {
		if (!members) {
			return;
		}
		const child = members.find(member => member.user?.isChild);
		const patient = members.find(member => member.user?.isPatient);
		const doctor = members.find(member => member.user?.isDoctor);
		const finalPatient = child ? child : patient;

		return {
			...preparePatientData(finalPatient?.user),
			...prepareDoctorData(doctor?.user),
			isConfirmedByPatient: finalPatient?.state === EventMemberState.Confirmed,
		};
	};

	//FUNCTION TO LOAD EVENTS FOR CALENDAR
	const loadCalendarData = async () => {
		setIsLoading(true);
		const tempEvents: CalendarEvent[] = [];
		if (dateRange) {
			const { data } = await getCalendarEvents({
				where: {
					start: dateRange.start.format(),
					end: dateRange.end.format(),
					calendarId: calendar.id,
				},
			});

			data.calendarEvents.data.forEach(item => {
				if (!item) return;

				const start = getMomentDate(item.dateFrom);
				const end = getMomentDate(item.dateTo);

				tempEvents.push({
					id: item.id,
					start: start.format(),
					end: end.format(),
					title: item.title as string,
					backgroundColor: item.layout.backgroundColor,
					borderColor: item.layout.backgroundColor,
					textColor: item.layout.fontColor,
					extendedProps: {
						workplace: item.calendar.workplace.doctorDisplayName,
						workplaceId: item.calendar.workplaceId,
						resourceId: item.calendar.workplaceId,
						note: item.description,
						meta: item.meta,
						createdByPatient: item.createdBy?.isPatient,
						caseId: item.caseId,
						...prepareEventMembersData(item.eventMembers),
					},
					allDay:
						start.isSame(start.clone().startOf('day')) &&
						end.isSame(end.clone().endOf('day')),
				});
			});
		}

		setEvents(tempEvents);
		setIsLoading(false);
	};

	useEffectAsync(async () => {
		await loadCalendarData();
	}, [dateRange]);

	const handleDateRangeChange = (start: Moment, end: Moment) => {
		setDateRange({ start, end });
	};

	return {
		reloadCalendarData: loadCalendarData,
		handleDateRangeChange,
		isLoading,
		events,
	};
}
